import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const inputClass =
  'w-full bg-transparent border-b-2 border-forest/20 focus:border-terracotta focus:outline-none py-3 text-forest placeholder-forest/40 transition-colors';
const labelClass = 'block text-xs tracking-[0.2em] uppercase text-forest/70 mb-2';

export function SignupPage() {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Land new accounts on the dashboard by default so they immediately
  // see their (empty) application slots and what they can do next.
  const from = (location.state as { from?: string } | null)?.from || '/dashboard';

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!email || !password) {
      setError('Email and password are required.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (honeypot.trim()) {
      // Silent honeypot — pretend success without doing anything.
      navigate('/', { replace: true });
      return;
    }
    setSubmitting(true);
    const res = await signup(email, password, name);
    setSubmitting(false);
    if (res.ok) {
      navigate(from, { replace: true });
    } else {
      setError(res.error);
    }
  }

  return (
    <div className="max-w-md mx-auto px-6 lg:px-12 py-20">
      <div className="text-xs tracking-[0.25em] uppercase text-terracotta mb-3">Join the beta</div>
      <h1
        className="text-4xl text-forest mb-3 leading-tight font-display"
        style={{ fontVariationSettings: '"opsz" 144' }}
      >
        Create your account.
      </h1>
      <p className="text-forest/70 mb-6 leading-relaxed">
        MowKind is in invite-only beta. No mowing services are being delivered yet —
        accounts let you test the platform and help us refine it before launch.
      </p>

      <form onSubmit={onSubmit} className="space-y-8">
        <div>
          <label className={labelClass}>Name (optional)</label>
          <input
            type="text"
            className={inputClass}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="What should we call you?"
            autoComplete="name"
          />
        </div>
        <div>
          <label className={labelClass}>Email *</label>
          <input
            type="email"
            className={inputClass}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            autoComplete="email"
            required
          />
        </div>
        <div>
          <label className={labelClass}>Password *</label>
          <input
            type="password"
            className={inputClass}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="At least 8 characters"
            autoComplete="new-password"
            required
            minLength={8}
          />
        </div>

        {/* Honeypot */}
        <input
          type="text"
          name="website"
          tabIndex={-1}
          autoComplete="off"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
          style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
          aria-hidden="true"
        />

        {error && (
          <div className="bg-terracotta/10 border-l-4 border-terracotta p-4 text-terracotta text-sm">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="bg-terracotta text-cream w-full px-8 py-4 text-sm tracking-[0.1em] uppercase hover:bg-forest transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? 'Creating account…' : 'Create account →'}
        </button>

        <p className="text-sm text-forest/60 text-center pt-4">
          Already have an account?{' '}
          <Link to="/login" className="text-terracotta hover:underline">
            Sign in
          </Link>
        </p>
      </form>
    </div>
  );
}
